import { clsx } from 'clsx'
import { type FC, type ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  type OfferPriceV1,
  type PimProductV11,
  PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../global-config'
import { priceGetFormattedPrice } from '../../../price'
import { TosSuperscript } from '../../../tos-superscripts'
import { priceAccessorShouldDisplayErxRecommendedRetailPrice } from '../../model/priceAccessor/priceAccessorShouldDisplayErxRecommendedRetailPrice'
import { priceAccessorShouldDisplayStrikeThroughPrice } from '../../model/priceAccessor/priceAccessorShouldDisplayStrikeThroughPrice'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'
import {
  type ProductPrices,
  useProductPrices,
} from '../ProductBuyBox/hooks/useProductPrices'

const getListPriceLabel = ({
  isOfferMirakl,
  isThirtyDayPriceTrendDown,
  showThirtyDaysPrice,
  superScripts,
}: {
  displayPrice: OfferPriceV1 | null
  isOfferMirakl: boolean
  isThirtyDayPriceTrendDown: boolean
  showThirtyDaysPrice: boolean
  superScripts: PublicGlobalConfig['superScripts']
}): ReactElement => {
  if (showThirtyDaysPrice && isThirtyDayPriceTrendDown) {
    return (
      <span className="whitespace-nowrap">
        <FormattedMessage id="general.label.thirtyDaysPrice" />
        <TosSuperscript value={superScripts.thirtyDaysPrice} />
      </span>
    )
  }

  return (
    <span className="whitespace-nowrap">
      {isOfferMirakl ? (
        <FormattedMessage id="general.label.miraklListPrice" />
      ) : (
        <FormattedMessage id="general.label.listPrice" />
      )}
      <TosSuperscript value={superScripts.avp} />
    </span>
  )
}

const isPriceStrikedThrough = ({
  displayPriceAmount,
  strikeThroughOfferPriceAmount,
}: {
  displayPriceAmount?: number
  strikeThroughOfferPriceAmount?: number
}): boolean => {
  return (
    !displayPriceAmount ||
    !strikeThroughOfferPriceAmount ||
    displayPriceAmount !== strikeThroughOfferPriceAmount
  )
}

const getOriginalPriceValue = ({
  erxRecommendedRetailPrice,
  isErxPriceDisplayed,
  shouldDisplayStrikeThroughPrice,
  strikeThroughOfferPrice,
}: {
  erxRecommendedRetailPrice: ProductPrices['erxRecommendedRetailPrice']
  isErxPriceDisplayed: boolean
  shouldDisplayStrikeThroughPrice: boolean
  strikeThroughOfferPrice: ProductPrices['strikeThroughOfferPrice']
}): number => {
  /**
   * WSAWA-4276
   * We show the AVP/UVP for ERX if there is no discount already present.
   * This is a failsafe for rare situations when we might have a discounted ERX product.
   * Even tough we've been told these products will never be discounted, it's better to have this scenario handled.
   */
  if (!shouldDisplayStrikeThroughPrice && isErxPriceDisplayed) {
    return erxRecommendedRetailPrice?.price ?? 0
  }

  return strikeThroughOfferPrice?.price ?? 0
}

export const ProductPriceOriginal: FC<{
  className?: string
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  'data-qa-id'?: string
  offerId?: string | null
  product: PimProductV11
}> = ({
  className,
  currentPrescriptionType,
  'data-qa-id': dataQaId,
  offerId,
  product,
}) => {
  const intl = useIntl()
  const { superScripts } = useGlobalConfigContext()
  const {
    displayPrice,
    erxRecommendedRetailPrice,
    isDiscountValid,
    isOfferMirakl,
    isThirtyDayPriceTrendDown,
    showThirtyDaysPrice,
    strikeThroughOfferPrice,
    thirtyDaysLowestPrice,
  } = useProductPrices({
    currentPrescriptionType,
    offerId,
    product,
  })

  if (
    !displayPrice ||
    currentPrescriptionType ===
      PrescriptionPrescriptionTypeEnum.PrivatePrescription
  ) {
    return null
  }

  const isPrescriptionProduct = productAccessorIsPrescriptionProduct(product)

  /*
   * WSAWA-4276
   * We show AVP/UVP price for ERX products that are either Free Prescription or Zuzahlungsbefreiung.
   * For Private Insurance/Privatrezept we don't need to show AVP/UVP since the price is the recommended retail price.
   */
  const isErxPriceDisplayed =
    priceAccessorShouldDisplayErxRecommendedRetailPrice({
      currentPrescriptionType,
      erxRecommendedRetailPrice,
      isPrescriptionProduct,
    })
  const shouldDisplayStrikeThroughPrice =
    priceAccessorShouldDisplayStrikeThroughPrice({
      currentPrescriptionType,
      displayPrice,
      isDiscountValid,
      strikeThroughOfferPrice,
    })

  const priceToShow =
    showThirtyDaysPrice && isThirtyDayPriceTrendDown
      ? (thirtyDaysLowestPrice?.amount ?? 0)
      : getOriginalPriceValue({
          erxRecommendedRetailPrice,
          isErxPriceDisplayed,
          shouldDisplayStrikeThroughPrice,
          strikeThroughOfferPrice,
        })

  // We don't show anything if the original price is lower that the displayed price
  if (priceToShow <= (displayPrice.price ?? 0)) {
    return null
  }

  return (
    <div className={clsx('flex gap-x-1', className)}>
      {getListPriceLabel({
        displayPrice,
        isOfferMirakl,
        isThirtyDayPriceTrendDown,
        showThirtyDaysPrice,
        superScripts,
      })}
      <span
        className={clsx(
          (!showThirtyDaysPrice || !isThirtyDayPriceTrendDown) &&
            isPriceStrikedThrough({
              displayPriceAmount: displayPrice.price,
              strikeThroughOfferPriceAmount: strikeThroughOfferPrice?.price,
            }) &&
            !isErxPriceDisplayed &&
            'line-through',
        )}
        data-qa-id={dataQaId ?? 'product-old-price'}
      >
        {priceGetFormattedPrice(priceToShow, intl)}
      </span>
    </div>
  )
}
